<template>
  <RichText v-if="fields?.heading?.value" class="text-xl-bold md:mb-5 mb-6" :field="fields?.heading" />
  <RichText v-if="noActiveProducts" class="text-base" :field="fields?.noActiveProductText" />
  <div v-else class="flex flex-col gap-6 md:gap-5 racq-max-w-xl5">
    <div v-if="!hideInsurance && portfolio?.insurancePolicies?.length" class="flex flex-col gap-6 md:gap-5">
      <div v-for="(insurance, index) in portfolio?.insurancePolicies" :key="index">
        <InsuranceProductCard
          :insuranceProduct="insurance"
          :fields="fields"
          :dataTestId="dataTestId(index, 'insurance')"
        />
      </div>
    </div>
    <div v-if="!hideAssistance && portfolio?.assistanceProducts?.length" class="flex flex-col gap-6 md:gap-5">
      <div v-for="(rsa, index) in portfolio?.assistanceProducts" :key="index">
        <RoadsideProductCard :assistanceProduct="rsa" :fields="fields" :dataTestId="dataTestId(index, 'roadside')" />
      </div>
    </div>
    <div v-if="!hideBank && portfolio?.bankingProducts?.length">
      <RoadsideProductCard :isBank="true" :fields="fields" :dataTestId="dataTestId(0, 'bank')" />
    </div>
  </div>
  <div v-if="fields?.crossSellPromo?.length" class="mt-6 flex flex-col gap-6 md:gap-5 racq-max-w-xl5">
    <div class="h-4 bg-racq-neutral-grey"></div>
    <div v-for="(value, index) in fields?.crossSellPromo" :key="index">
      <PromoCardImage :fields="{ items: [value] }" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { Portfolio, ProductContentFields } from '@/interfaces';
import InsuranceProductCard from '../InsuranceProductCard/InsuranceProductCard.vue';
import { RichText } from '@sitecore-jss/sitecore-jss-vue';
import RoadsideProductCard from '../RoadsideProductCard/RoadsideProductCard.vue';
import { computed } from 'vue';
import PromoCardImage from '../PromoCardImage/PromoCardImage.vue';

export interface IProductCardList {
  hideAssistance?: boolean;
  hideInsurance?: boolean;
  hideBank?: boolean;
  portfolio?: Portfolio;
  fields?: ProductContentFields;
}
const props = defineProps<IProductCardList>();
const noActiveProducts = computed(() => {
  const noInsurance = (props.portfolio?.insurancePolicies?.length ?? 0) === 0;
  const noAssistance = (props.portfolio?.assistanceProducts?.length ?? 0) === 0;
  const noBank = (props.portfolio?.bankingProducts?.length ?? 0) === 0;

  const hideInsurance = !!props.hideInsurance;
  const hideAssistance = !!props.hideAssistance;
  const hideBank = !!props.hideBank;

  // True iff every *visible* category has zero items.
  // Hidden categories are ignored.
  return (hideInsurance || noInsurance) && (hideAssistance || noAssistance) && (hideBank || noBank);
});

/**
 * To calculate product tile index for analytics
 * @param index
 * @param type
 */
const dataTestId = (index: number, type: 'insurance' | 'bank' | 'roadside') => {
  let i = 0;
  switch (type) {
    case 'insurance':
      i = index;
      break;
    case 'roadside':
      i = index + (props.hideInsurance ? 0 : (props.portfolio?.insurancePolicies?.length ?? 0));
      break;
    case 'bank':
      i =
        index +
        (props.hideInsurance ? 0 : (props.portfolio?.insurancePolicies?.length ?? 0)) +
        (props.hideAssistance ? 0 : (props.portfolio?.assistanceProducts?.length ?? 0));
      break;
    default:
      break;
  }
  return `product_tile_${i}`;
};
</script>
